import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — Bulk Transfer + Daily Wishes  (Phase 5.4 A, B)
//
// Bulk Transfer is the screen run the day somebody leaves, and it cannot be
// undone short of restoring a backup. So the flow is deliberately two-step:
// preview the counts, then confirm. The transfer button does nothing until a
// preview for the CURRENT selection exists — changing any input clears it, so
// nobody can confirm a count that describes a different query.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftRight, Cake, AlertTriangle, CheckCircle2, Users, History,
} from 'lucide-react';

type Tab = 'transfer' | 'wishes';

const num = (v: unknown) => Number(v || 0).toLocaleString('en-IN');

export function AdminToolsPage() {
  const [tab, setTab] = useState<Tab>('transfer');
  return (
    <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
      <PageHeader icon={ArrowLeftRight} eyebrow="Administration" title="Admin Tools" description={<>Reassign a departing employee's records, and configure automated wishes.</>} />

      <div className="flex gap-6 border-b border-outline text-xs font-semibold">
        <button onClick={() => setTab('transfer')}
          className={`pb-3 flex items-center gap-2 border-b-2 ${tab === 'transfer' ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant'}`}>
          <ArrowLeftRight className="w-4 h-4" /> Bulk Transfer
        </button>
        <button onClick={() => setTab('wishes')}
          className={`pb-3 flex items-center gap-2 border-b-2 ${tab === 'wishes' ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant'}`}>
          <Cake className="w-4 h-4" /> Daily Wishes
        </button>
      </div>

      {tab === 'transfer' ? <TransferTab /> : <WishesTab />}
    </div>
  );
}

// ── Bulk Transfer ────────────────────────────────────────────────────────────
function TransferTab() {
  const qc = useQueryClient();
  const [fromUserId, setFrom] = useState('');
  const [toUserId, setTo] = useState('');
  const [modules, setModules] = useState<string[]>([]);
  const [from, setFromDate] = useState('');
  const [to, setToDate] = useState('');
  const [preview, setPreview] = useState<any>(null);

  const { data: mods } = useQuery({
    queryKey: ['admin-tools', 'modules'],
    queryFn: async () => (await api.get('/api/admin-tools/transfer/modules')).data,
  });
  // Deliberately NOT /api/users: that returns active users only, and the
  // person whose records need moving has usually already been deactivated.
  const { data: users } = useQuery({
    queryKey: ['admin-tools', 'transfer-users'],
    queryFn: async () => (await api.get('/api/admin-tools/transfer/users')).data,
  });
  const { data: history } = useQuery({
    queryKey: ['admin-tools', 'history'],
    queryFn: async () => (await api.get('/api/admin-tools/transfer/history')).data,
  });

  // Any change to the query invalidates the preview. Confirming a count that
  // describes a different selection is the way this feature moves the wrong
  // records, so the button is gated on a preview matching what is on screen.
  useEffect(() => { setPreview(null); }, [fromUserId, toUserId, modules, from, to]);

  const body = () => ({ fromUserId, toUserId, modules, from: from || null, to: to || null });

  const doPreview = useMutation({
    mutationFn: async () => (await api.post('/api/admin-tools/transfer/preview', body())).data,
    onSuccess: setPreview,
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not preview.'),
  });

  const doTransfer = useMutation({
    mutationFn: async () => (await api.post('/api/admin-tools/transfer', body())).data,
    onSuccess: (d: any) => {
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['admin-tools', 'history'] });
      if (d.complete) toast.success(`${num(d.totalMoved)} record(s) transferred.`);
      // A shortfall means records are still pointing at the departing user.
      // Reported as a warning, never folded into a success message.
      else toast.error(`${num(d.totalMoved)} of ${num(d.totalMatched)} moved — ${num(d.shortfall)} did not. Check the history.`, { duration: 8000 });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Transfer failed.'),
  });

  const userList: any[] = users?.users || [];
  const activeUsers = userList.filter((u: any) => u.isActive);
  const sameUser = Boolean(fromUserId) && fromUserId === toUserId;
  const canPreview = Boolean(fromUserId && toUserId && modules.length && !sameUser);

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-semibold block mb-1">Transfer FROM</span>
          <select value={fromUserId} onChange={(e) => setFrom(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm">
            <option value="">Select the departing user…</option>
            {userList.map((u: any) => (
              <option key={u.id} value={u.id}>
                {u.display_name}{u.isActive ? '' : ' (deactivated)'}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold block mb-1">Transfer TO</span>
          {/* Only active users: moving a book of work onto a disabled account
              hides it from everyone, which is what this feature prevents. */}
          <select value={toUserId} onChange={(e) => setTo(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm">
            <option value="">Select who takes them over…</option>
            {activeUsers.map((u: any) => (
              <option key={u.id} value={u.id}>{u.display_name}</option>
            ))}
          </select>
        </label>
      </div>

      {sameUser && (
        <p className="text-xs text-danger-text dark:text-danger-text">
          The source and target are the same user.
        </p>
      )}

      <div>
        <span className="text-xs font-semibold block mb-2">Modules to transfer</span>
        <div className="flex flex-wrap gap-2">
          {(mods?.modules || []).map((m: any) => {
            const on = modules.includes(m.key);
            return (
              <button key={m.key} type="button"
                onClick={() => setModules(on ? modules.filter((x) => x !== m.key) : [...modules, m.key])}
                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                  on ? 'border-primary text-primary bg-primary/10' : 'border-outline text-on-surface-variant'
                }`}>
                {m.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-on-surface-variant mt-2">
          Nothing is selected by default — a bulk reassignment is not something to do by accident.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-semibold block mb-1">Created from (optional)</span>
          <input type="date" value={from} onChange={(e) => setFromDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold block mb-1">Created to (optional)</span>
          <input type="date" value={to} onChange={(e) => setToDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => doPreview.mutate()} disabled={!canPreview || doPreview.isPending}
          className="px-4 py-2 rounded-lg border border-outline text-sm font-semibold disabled:opacity-40">
          Preview
        </button>
        <button onClick={() => doTransfer.mutate()} disabled={!preview || doTransfer.isPending}
          title={!preview ? 'Preview first' : undefined}
          className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold disabled:opacity-40">
          {preview ? `Transfer ${num(preview.totalMatched)} record(s)` : 'Transfer'}
        </button>
      </div>

      {preview && (
        <div className="rounded-xl border border-outline bg-surface p-4">
          <p className="text-sm font-semibold mb-2">
            {num(preview.totalMatched)} record(s) would move
          </p>
          <div className="grid sm:grid-cols-3 gap-2 text-xs">
            {preview.results.map((r: any) => (
              <div key={r.module} className="flex justify-between border border-outline rounded-lg px-3 py-2">
                <span className="text-on-surface-variant">{r.label}</span>
                <span className="font-semibold">{num(r.matched)}</span>
              </div>
            ))}
          </div>
          {preview.totalMatched === 0 && (
            <p className="text-xs text-warning-text dark:text-warning-text mt-3 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Nothing matched. Check the date range before concluding this user owns no records.
            </p>
          )}
          <p className="text-[11px] text-on-surface-variant mt-3">
            This cannot be undone. Every moved record is logged individually under History.
          </p>
        </div>
      )}

      {(history?.items || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
            <History className="w-4 h-4" /> Recent transfers
          </h3>
          <div className="space-y-2">
            {history.items.slice(0, 5).map((h: any) => (
              <div key={h._id} className="rounded-lg border border-outline px-3 py-2 text-xs flex flex-wrap justify-between gap-2">
                <span>
                  <span className="font-semibold">{h.fromUserName}</span> → <span className="font-semibold">{h.toUserName}</span>
                  <span className="text-on-surface-variant"> · {h.modules?.join(', ')}</span>
                </span>
                <span className={h.complete ? 'text-success-text dark:text-success-text' : 'text-danger-text dark:text-danger-text'}>
                  {num(h.totalMoved)}/{num(h.totalMatched)} moved
                  {!h.complete && ' — incomplete'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Daily Wishes ─────────────────────────────────────────────────────────────
function WishesTab() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin-tools', 'wishes'],
    queryFn: async () => (await api.get('/api/admin-tools/wishes/settings')).data,
  });
  const { data: due } = useQuery({
    queryKey: ['admin-tools', 'wishes-due'],
    queryFn: async () => (await api.get('/api/admin-tools/wishes/due')).data,
  });
  const { data: log } = useQuery({
    queryKey: ['admin-tools', 'wishes-log'],
    queryFn: async () => (await api.get('/api/admin-tools/wishes/log?limit=20')).data,
  });

  const s = data?.settings;
  const save = useMutation({
    mutationFn: async (patch: any) => (await api.put('/api/admin-tools/wishes/settings', patch)).data,
    onSuccess: () => {
      toast.success('Saved.');
      qc.invalidateQueries({ queryKey: ['admin-tools', 'wishes'] });
      qc.invalidateQueries({ queryKey: ['admin-tools', 'wishes-due'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not save.'),
  });
  const runNow = useMutation({
    mutationFn: async () => (await api.post('/api/admin-tools/wishes/run')).data,
    onSuccess: (d: any) => {
      toast.success(d.ran
        ? `Sent ${num(d.sent)}, skipped ${num(d.skipped)}, failed ${num(d.failed)}.`
        : d.reason);
      qc.invalidateQueries({ queryKey: ['admin-tools', 'wishes-log'] });
      qc.invalidateQueries({ queryKey: ['admin-tools', 'wishes-due'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Run failed.'),
  });

  if (!s) return <p className="text-sm text-on-surface-variant">Loading…</p>;

  const anyOn = s.birthdayEnabled || s.anniversaryEnabled;
  const unwired = ['sms', 'whatsapp'].filter((c) => s.channels?.[c]);

  return (
    <div className="space-y-5">
      {!anyOn && (
        <div className="p-3 rounded-xl bg-surface-variant/40 border border-outline text-xs text-on-surface-variant">
          Daily Wishes is off. Nothing is sent to any contact until an occasion is switched on here.
        </div>
      )}

      <div className="space-y-3">
        <Toggle label="Birthday wishes" checked={s.birthdayEnabled}
          onChange={(v) => save.mutate({ birthdayEnabled: v })} />
        <Toggle label="Anniversary wishes" checked={s.anniversaryEnabled}
          onChange={(v) => save.mutate({ anniversaryEnabled: v })} />
        <Toggle label="Include contacts" checked={s.includeContacts}
          onChange={(v) => save.mutate({ includeContacts: v })} />
        <Toggle label="Include employees" checked={s.includeEmployees}
          onChange={(v) => save.mutate({ includeEmployees: v })} />
      </div>

      <div>
        <span className="text-xs font-semibold block mb-2">Channels</span>
        <div className="space-y-2">
          <Toggle label="Email" checked={s.channels?.email}
            onChange={(v) => save.mutate({ channels: { email: v } })} />
          <Toggle label="SMS" checked={s.channels?.sms}
            onChange={(v) => save.mutate({ channels: { sms: v } })} />
          <Toggle label="WhatsApp" checked={s.channels?.whatsapp}
            onChange={(v) => save.mutate({ channels: { whatsapp: v } })} />
        </div>
        {unwired.length > 0 && (
          // Stated plainly rather than letting the toggle imply it works.
          <p className="text-xs text-warning-text dark:text-warning-text mt-2 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {unwired.join(' and ')} {unwired.length > 1 ? 'are' : 'is'} switched on but no gateway is
            configured, so nothing is sent on {unwired.length > 1 ? 'those channels' : 'that channel'}.
          </p>
        )}
      </div>

      <label className="block max-w-xs">
        <span className="text-xs font-semibold block mb-1">Send at</span>
        <input type="time" defaultValue={s.sendTime}
          onBlur={(e) => e.target.value !== s.sendTime && save.mutate({ sendTime: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
      </label>

      {(['birthday', 'anniversary'] as const).map((occ) => (
        <label key={occ} className="block">
          <span className="text-xs font-semibold block mb-1 capitalize">{occ} message</span>
          <textarea defaultValue={s.templates?.[occ]} rows={4}
            onBlur={(e) => e.target.value !== s.templates?.[occ]
              && save.mutate({ templates: { [occ]: e.target.value } })}
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-xs font-mono" />
        </label>
      ))}
      <p className="text-[11px] text-on-surface-variant -mt-3">
        {'{{firstName}} {{name}} {{senderName}} {{companyName}}'} — anything else is removed rather
        than shown to the customer.
      </p>

      <div className="rounded-xl border border-outline bg-surface p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> {num((due?.due || []).length)} due today
          </p>
          <button onClick={() => runNow.mutate()} disabled={runNow.isPending}
            className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold disabled:opacity-40">
            Run now
          </button>
        </div>
        {(due?.due || []).length > 0 && (
          <ul className="mt-2 text-xs text-on-surface-variant space-y-0.5 max-h-32 overflow-y-auto">
            {due.due.map((d: any) => (
              <li key={d.key}>{d.name} — {d.occasion}{d.email ? '' : ' (no email address)'}</li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-on-surface-variant mt-2">
          Running twice does not message anyone twice.
        </p>
      </div>

      {(log?.items || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Recent sends</h3>
          <div className="space-y-1">
            {log.items.slice(0, 10).map((l: any) => (
              <div key={l._id} className="text-xs flex justify-between border border-outline rounded-lg px-3 py-1.5">
                <span>{l.recipient || '(no address)'} · {l.occasion}</span>
                <span className={l.status === 'sent' ? 'text-success-text dark:text-success-text'
                  : l.status === 'failed' ? 'text-danger-text dark:text-danger-text' : 'text-on-surface-variant'}>
                  {l.status}{l.error ? ` — ${l.error}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange }: {
  label: string; checked?: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} />
      {label}
      {checked && <CheckCircle2 className="w-3.5 h-3.5 text-success-text" />}
    </label>
  );
}

export default AdminToolsPage;
